import React, { useState, useMemo, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Search, Package, Plus, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  useProductSearch,
  useProductsByCategory,
} from '@/features/product-library/hooks/useProductLibrary';
import type { ProductLibraryItem, ProductCategory } from '@/features/product-library/types';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

interface GroupProductSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddProducts: (productIds: string[]) => void;
  groupName: string;
  existingProductIds?: string[];
  isLoading?: boolean;
}

const CATEGORY_LABELS: Record<ProductCategory | 'all', string> = {
  all: 'All Products',
  fixture: 'Lighting Fixtures',
  control: 'Lighting Controls',
  speaker: 'Speakers',
  access_point: 'Access Points',
  camera: 'Cameras',
  touch_panel: 'Touch Panels',
  amplifier: 'Amplifiers',
  processor: 'Processors',
  switch: 'Network Switches',
  doorbell: 'Doorbells',
  sensor: 'Sensors',
  other: 'Other',
};

const LV_CATEGORIES: ProductCategory[] = [
  'camera',
  'speaker',
  'access_point',
  'touch_panel',
  'doorbell',
  'sensor',
  'amplifier',
  'processor',
  'switch',
  'other',
];

export function GroupProductSelectionModal({
  open,
  onOpenChange,
  onAddProducts,
  groupName,
  existingProductIds = [],
  isLoading = false,
}: GroupProductSelectionModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<'all' | ProductCategory>('all');
  const [selectedProducts, setSelectedProducts] = useState<Map<string, number>>(new Map());

  // Search products
  const { data: searchResults, isLoading: isSearching } = useProductSearch({
    query: searchQuery,
    is_public: true,
    limit: 200,
  });

  // Get all products when showing all - direct query without search
  const { data: allProductsData, isLoading: isLoadingAll } = useQuery({
    queryKey: ['all-group-products'],
    queryFn: async () => {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('product_library')
        .select('*')
        .eq('is_public', true)
        .order('brand', { ascending: true })
        .order('model', { ascending: true })
        .limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: selectedTab === 'all' && open,
  });

  // Get products by selected category tab
  const { data: categoryProducts, isLoading: isLoadingCategory } = useProductsByCategory(
    selectedTab,
    200,
  );

  // Determine which products to show
  const filteredProducts = useMemo(() => {
    let products: ProductLibraryItem[] = [];

    if (searchQuery && searchResults) {
      products = searchResults.items;
    } else if (selectedTab === 'all' && allProductsData) {
      products = allProductsData;
    } else if (categoryProducts) {
      products = categoryProducts;
    }

    // Don't filter out already added products - allow multiple instances
    return products;
  }, [searchQuery, searchResults, categoryProducts, selectedTab, allProductsData]);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped = new Map<ProductCategory, ProductLibraryItem[]>();

    filteredProducts.forEach(product => {
      const category = product.category as ProductCategory;
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(product);
    });

    return grouped;
  }, [filteredProducts]);

  const isLoadingProducts = searchQuery
    ? isSearching
    : selectedTab === 'all'
      ? isLoadingAll
      : isLoadingCategory;

  // Debug all products
  useEffect(() => {
    if (selectedTab === 'all') {
      logger.log('All products query:', {
        allProductsData,
        isLoadingAll,
        filteredProductsLength: filteredProducts.length,
        filteredProducts,
      });
    }
  }, [allProductsData, isLoadingAll, selectedTab, filteredProducts]);

  // Reset selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedProducts(new Map());
      setSearchQuery('');
      setSelectedTab('all');
    }
  }, [open]);

  const handleToggleProduct = (productId: string) => {
    setSelectedProducts(prev => {
      const next = new Map(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.set(productId, 1);
      }
      return next;
    });
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedProducts(prev => {
        const next = new Map(prev);
        next.delete(productId);
        return next;
      });
    } else {
      setSelectedProducts(prev => {
        const next = new Map(prev);
        next.set(productId, quantity);
        return next;
      });
    }
  };

  const handleSelectAll = (category: ProductCategory) => {
    const products = productsByCategory.get(category) || [];
    const allSelected = products.every(p => selectedProducts.has(p.id));

    setSelectedProducts(prev => {
      const next = new Map(prev);
      products.forEach(product => {
        if (allSelected) {
          next.delete(product.id);
        } else {
          next.set(product.id, 1);
        }
      });
      return next;
    });
  };

  const handleAddProducts = async () => {
    if (selectedProducts.size > 0) {
      // Add products based on their quantities
      const productIds: string[] = [];
      selectedProducts.forEach((quantity, productId) => {
        for (let i = 0; i < quantity; i++) {
          productIds.push(productId);
        }
      });
      await onAddProducts(productIds);
      onOpenChange(false);
    }
  };

  // Calculate total items to add
  const totalItemCount = Array.from(selectedProducts.values()).reduce((sum, qty) => sum + qty, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl h-[80vh] flex flex-col"
        aria-describedby="group-product-selection-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Add Products to {groupName}
          </DialogTitle>
        </DialogHeader>

        <p id="group-product-selection-description" className="text-sm text-muted-foreground">
          Select products from the library to add to this group
        </p>

        <div className="flex-1 flex flex-col space-y-4 min-h-0">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by brand, model, or description..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              className="pl-10"
            />
          </div>

          {/* Category Tabs */}
          <Tabs
            value={selectedTab}
            onValueChange={value => setSelectedTab(value as 'all' | ProductCategory)}
          >
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="camera">Cameras</TabsTrigger>
              <TabsTrigger value="speaker">Speakers</TabsTrigger>
              <TabsTrigger value="access_point">Access Points</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Product List */}
          <div className="border rounded-lg flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1">
              {isLoadingProducts ? (
                <div className="p-4 space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {searchQuery
                    ? 'No products found matching your search'
                    : existingProductIds.length > 0
                      ? 'All available products have already been added to this group'
                      : 'No products available in this category'}
                </div>
              ) : (
                <div className="p-4 space-y-6">
                  {selectedTab === 'all' ? (
                    // Show grouped by category
                    Array.from(productsByCategory.entries()).map(([category, products]) => (
                      <div key={category} className="space-y-2">
                        <div className="flex items-center justify-between sticky top-0 bg-background py-2">
                          <h3 className="font-medium text-sm">
                            {CATEGORY_LABELS[category]} ({products.length})
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSelectAll(category)}
                            className="text-xs"
                          >
                            {products.every(p => selectedProducts.has(p.id))
                              ? 'Deselect All'
                              : 'Select All'}
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {products.map(product => (
                            <ProductItem
                              key={product.id}
                              product={product}
                              quantity={selectedProducts.get(product.id) || 0}
                              onToggle={() => handleToggleProduct(product.id)}
                              onQuantityChange={qty => handleQuantityChange(product.id, qty)}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    // Show flat list for single category
                    <div className="space-y-2">
                      {filteredProducts.map(product => (
                        <ProductItem
                          key={product.id}
                          product={product}
                          quantity={selectedProducts.get(product.id) || 0}
                          onToggle={() => handleToggleProduct(product.id)}
                          onQuantityChange={qty => handleQuantityChange(product.id, qty)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Selection Count */}
            {selectedProducts.size > 0 && (
              <div className="p-4 border-t bg-muted/50">
                <p className="text-sm font-medium">
                  {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected (
                  {totalItemCount} total item{totalItemCount !== 1 ? 's' : ''})
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddProducts} disabled={selectedProducts.size === 0 || isLoading}>
            <Plus className="h-4 w-4 mr-2" />
            Add Selected ({totalItemCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ProductItemProps {
  product: ProductLibraryItem;
  quantity: number;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
}

function ProductItem({ product, quantity, onToggle, onQuantityChange }: ProductItemProps) {
  const isSelected = quantity > 0;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg transition-colors border',
        isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted border-transparent',
      )}
    >
      <Checkbox checked={isSelected} onCheckedChange={onToggle} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm truncate">
            {product.brand} {product.model}
          </h4>
          {product.is_verified && (
            <Badge variant="secondary" className="text-xs shrink-0">
              Verified
            </Badge>
          )}
        </div>

        {product.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{product.description}</p>
        )}

        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
          {product.part_number && <span className="font-mono">{product.part_number}</span>}
          {product.price && (
            <span className="font-medium text-foreground">${product.price.toFixed(2)}</span>
          )}
        </div>
      </div>

      {/* Quantity controls */}
      {isSelected && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={e => {
              e.stopPropagation();
              onQuantityChange(Math.max(0, quantity - 1));
            }}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-8 text-center font-medium">{quantity}</span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={e => {
              e.stopPropagation();
              onQuantityChange(quantity + 1);
            }}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
